import React, { useState, useRef, useEffect } from 'react';
import { formatFileSize, downloadFile, formatDuration, generateMockWaveform, getWaveformHeights } from '../../utils/mediaHelpers';
import { useBinaryMedia } from '../../utils/binaryMediaFetcher';

const AudioMessage = ({ media, caption, isSender, category }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolume, setShowVolume] = useState(false);
  const [waveformData, setWaveformData] = useState([]);
  const [audioError, setAudioError] = useState(false);

  const audioRef = useRef(null);

  // Use binary media hook for proper blob fetching
  const { url: audioUrl, blobSize, isLoading: fetchLoading, error: fetchError, errorDetails } = useBinaryMedia(
    media?.url,
    'audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/webm,*/*'
  );

  // Audio event handlers - only attach after binary blob is loaded
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && audio.duration !== Infinity) {
        setDuration(audio.duration);
        // Generate mock waveform data based on duration
        const mockWaveform = generateMockWaveform(audio.duration, 30);
        setWaveformData(mockWaveform);
      }
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setAudioError(true);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(error => {
        console.error('Play failed:', error);
        setAudioError(true);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const seekTime = (e.nativeEvent.offsetX / e.target.offsetWidth) * duration;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume;
    }
  };

  const handleDownload = () => {
    downloadFile(media.url, media.originalName);
  };

  const formatTime = (time) => {
    return formatDuration(time);
  };

  const calculateProgress = () => {
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  };

  // Get waveform heights with active state
  const waveformHeights = getWaveformHeights(waveformData, currentTime, duration);

  const isLoading = fetchLoading || (audioUrl && !duration);

  return (
    <div className={`media-message audio-message ${isSender ? 'sender' : 'receiver'}`} style={{ width: '100%', maxWidth: '300px' }}>
      {/* Hidden audio element with binary blob URL */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
        />
      )}

      <div className="media-content">
        {/* Loading state */}
        {isLoading && (
          <div className="audio-loading">
            <div className="loading-spinner"></div>
            <span>Loading audio...</span>
            {blobSize > 0 && (
              <div className="audio-info">
                <small>Size: {formatFileSize(blobSize)}</small>
              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {(audioError || fetchError) && (
          <div className="audio-error">
            <div className="error-icon">❌</div>
            <span>Failed to load audio</span>
            {errorDetails && (
              <div className="error-details">
                <small>{errorDetails}</small>
              </div>
            )}
            <div className="error-actions">
              <button
                className="download-fallback-btn"
                onClick={handleDownload}
                title="Download audio file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                Download Audio
              </button>
            </div>
          </div>
        )}

        {!isLoading && !audioError && audioUrl && (
          <div className="audio-player">
            {/* Play/Pause button */}
            <button
              className={`audio-play-btn ${isPlaying ? 'playing' : ''}`}
              onClick={togglePlay}
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Waveform visualization */}
            <div className="audio-waveform-container" onClick={handleSeek}>
              <div className="audio-waveform">
                {waveformHeights.map((bar, index) => (
                  <div
                    key={index}
                    className={`waveform-bar ${bar.isActive ? 'active' : ''}`}
                    style={{
                      height: `${bar.height * 100}%`,
                      animationDelay: `${index * 0.03}s`
                    }}
                  />
                ))}
              </div>
              <div className="audio-progress-overlay" style={{ width: `${calculateProgress()}%` }} />
            </div>

            {/* Time display */}
            <div className="audio-time">
              <span>{formatTime(currentTime)}</span>
              <span>/</span>
              <span>{formatTime(duration)}</span>
            </div>

            {/* Volume control */}
            <div className="audio-volume-container">
              <button
                className="audio-volume-btn"
                onClick={() => setShowVolume(!showVolume)}
                title="Volume"
              >
                {volume === 0 ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 9H6c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h3.29l4.71 4.71c.62.62 1.7.18 1.7-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91v2.06c1.3-.35 2.48-.97 3.47-1.81l1.29 1.29c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L5.05 3.63a.996.996 0 00-1.41 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v2.06c2.89.86 5 3.54 5 6.58z" />
                  </svg>
                ) : volume < 0.5 ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>

              {showVolume && (
                <div className="audio-volume-slider">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                </div>
              )}
            </div>

            {/* Download button */}
            <button
              className="media-download-btn"
              onClick={handleDownload}
              title="Download audio"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Metadata - timestamp only */}
      <div className="media-info">
        <div className="media-metadata">
          {blobSize > 0 ? (
            <span className="file-size">{formatFileSize(blobSize)}</span>
          ) : media.size > 0 ? (
            <span className="file-size">{formatFileSize(media.size)}</span>
          ) : null}
        </div>
        <div className="message-time">
          {new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
};

export default AudioMessage;
